import './GeneticCounseling.scss';

import { useContext, useEffect, useMemo, useState } from 'react';
import { Breadcrumb, BreadcrumbItemProps, Button, Form } from 'react-bootstrap';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { HOMEPAGE, ORDERS, REPORTS, RESULTS } from '@constants/routes';
import { Order } from '@customTypes/order';
import ContentContainer from '@layouts/container/ContentContainer';
import PageContainer from '@layouts/container/PageContainer';
import { fetchGeneticCounselingDetails, fetchRequisitionDetailsById } from '@store/actions';
import { Context } from '@store/store';

const GeneticCounseling = () => {
  const [state, dispatch] = useContext(Context);
  const [loading, setLoading] = useState(false);
  const { reqId: currentReqId } = useParams<{ reqId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Set when a guardian arrives here from a minor's results tab; the access code
  // below then has to be that minor's rather than the logged-in user's.
  const viewing = searchParams.get('viewing') || undefined;
  const [viewedAccessCode, setViewedAccessCode] = useState('');
  const [viewedLoading, setViewedLoading] = useState(false);

  const breadcrumbs = useMemo(() => {
    const fromResults = location.state?.pathname === RESULTS || location.state?.pathname === HOMEPAGE;
    if (fromResults) {
      const resultsTo = viewing ? `${RESULTS}?individual=${encodeURIComponent(viewing)}` : RESULTS;
      return [
        {
          children: 'My Results',
          as: Link,
          to: resultsTo,
        },
        {
          children: 'Genetic Counseling',
          active: true,
        },
      ];
    }
    return [
      {
        children: 'My Orders',
        as: Link,
        to: ORDERS,
      },
      {
        children: 'Order Details',
        as: Link,
        to: `${REPORTS}/${currentReqId}`,
      },
      {
        children: 'Genetic Counseling',
        active: true,
      },
    ] as BreadcrumbItemProps[];
  }, [location.state, currentReqId, viewing]);
  const testNames: string[] = useMemo(
    () => (state.currentTestRequest?.orders || []).map((o: Order) => o.product_name),
    [state.currentTestRequest],
  );

  useEffect(() => {
    setLoading(true);
    fetchRequisitionDetailsById(currentReqId!).then(res => {
      dispatch({
        type: 'SET_CURRENT_TEST_REQUEST',
        payload: res,
      });
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clear any prior minor's code up front so a stale one can never be shown or copied.
    setViewedAccessCode('');
    if (!viewing) {
      setViewedLoading(false);
      return;
    }
    let isCurrent = true;
    setViewedLoading(true);
    fetchGeneticCounselingDetails(viewing, { suppressErrorToast: true })
      .then(details => {
        if (isCurrent) setViewedAccessCode(details?.access_code || '');
      })
      .catch(err => console.error('Error fetching genetic counseling details:', err))
      .finally(() => {
        if (isCurrent) setViewedLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [viewing]);

  const accessCode = viewing ? viewedAccessCode : state.gcAccessCode;
  // Gate Copy / Schedule on a resolved, non-empty code so neither acts on a blank value.
  const isAccessCodeReady = !!accessCode && !loading && !viewedLoading;

  if (!accessCode && !state.testRequests.length) return null;

  return (
    <PageContainer>
      <ContentContainer containerClass='bg-body py-4 genetic-counseling-header'>
        <Breadcrumb listProps={{ className: 'mb-0' }}>
          {breadcrumbs.map((breadcrumb, index) => (
            <Breadcrumb.Item key={index} {...breadcrumb} />
          ))}
        </Breadcrumb>
        <h1 className='text-primary mb-3'>Genetic Counseling</h1>
        <p className='text-secondary'>Receive post-test genetic counseling at no additional cost to you.</p>
      </ContentContainer>

      <ContentContainer containerClass='pt-4 genetic-counseling-body'>
        <div className='d-flex flex-column gap-4'>
          <div>
            <h4 className='text-primary pb-2 border-bottom fw-semibold mb-0'>Expert genetic guidance awaits</h4>
            <p className='mt-3'>
              Understanding your genetics can be complicated. We have partnered with DNAvisit to provide complimentary
              genetic counseling to get the most out of your MyOme Personal Genome experience
            </p>
          </div>

          <div className='gap-3 section-container'>
            <div className='gap-1'>
              <h6 className='font-family-custom lh-base text-primary fw-semibold mb-0'>Schedule your appointment</h6>
              <p className='fs-sm'>
                Use your one time access code to schedule your appointment by clicking the button below.
              </p>
              <p className='fs-xs'>
                By clicking &quot;Schedule Now&quot;, you will be taken to a third-party website to schedule with our
                partner, DNAvisit.
              </p>
            </div>
            <div className='d-flex flex-column align-items-start flex-sm-row align-items-sm-center gap-4'>
              <div className='position-relative access-code-wrapper'>
                <Form.Control value={accessCode} aria-label='access code' disabled readOnly />
                <FontAwesomeIcon
                  icon={faCopy}
                  aria-disabled={!isAccessCodeReady}
                  style={{
                    pointerEvents: isAccessCodeReady ? undefined : 'none',
                    opacity: isAccessCodeReady ? 1 : 0.5,
                  }}
                  onClick={() => isAccessCodeReady && navigator.clipboard.writeText(accessCode)}
                />
              </div>
              <Button
                size='sm'
                className='btn btn-primary text-nowrap'
                onClick={() => (window.location.href = 'https://www.DNAvisit.com/appointment')}
                disabled={!isAccessCodeReady}
              >
                Schedule now
              </Button>
            </div>
          </div>

          <div className='gap-1 section-container'>
            <h6 className='font-family-custom lh-base text-primary fw-semibold mb-0'>Available Reports</h6>
            <p className='fs-sm'>You will be receiving genetic counseling for the reports listed below.</p>
            {testNames.length > 0 && (
              <ul className='mb-0'>
                {testNames.map((test, i) => (
                  <li key={`${test}-${i}`} className='fs-xs'>
                    {test}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </ContentContainer>
    </PageContainer>
  );
};

export default GeneticCounseling;
